/**
 * Tool definitions: cost basis, tax lots and corporate-action review.
 *
 * Reads expose what the Cost Basis page shows (per-holding calculated vs
 * stored basis, open lots, per-sale lot assignments, corporate actions the
 * lot engine could not resolve). Writes are the same commands that page uses:
 * per-sale lot method / specific-lot picks, lot recalculation, holding
 * cost-basis source overrides, manual lots, and corporate-action resolution.
 */

import { LotMatchingMethod } from '../../enums/LotMatchingMethod.js';
import type { ToolDefinition } from './types.js';

const LOT_METHODS = Object.values(LotMatchingMethod);
const COST_BASIS_SOURCES = ['imported', 'calculated', 'manual'];

export const costBasisTools = {
  get_cost_basis_report: {
    name: 'get_cost_basis_report',
    access: 'read',
    description:
      "Cost basis report for one account: for each open holding, the stored cost basis and its source (imported from the broker, calculated from transactions, or manual), the basis calculated from transaction history with its open lots, whether the history is complete, calculator warnings, and needsReview (not yet reviewed, or stored and calculated basis differ by more than $0.01). Open lots with isManual=true are manual lots; their transactionId is the manual lot ID used by remove_manual_lot. Start here when working through an account's cost basis.",
    inputSchema: {
      type: 'object',
      properties: {
        accountId: { type: 'string', description: 'Luminaria account UUID.' },
        symbol: {
          type: 'string',
          description: 'Optional symbol to limit the report to one holding (case-insensitive).',
        },
        includeTransactions: {
          type: 'boolean',
          description:
            'Include each holding\'s related transactions (the stock plus options on it). Defaults to false; the report can be large.',
        },
      },
      required: ['accountId'],
    },
  },
  get_symbol_lot_assignments: {
    name: 'get_symbol_lot_assignments',
    access: 'read',
    description:
      'Every sale of a symbol in an account with the lots it consumed: per lot the acquisition date, quantity, basis, proceeds, holding term, lot-matching method, wash-sale flags/disallowed loss, and whether the user overrode the assignment. Includes total basis, proceeds and gain per sale. Use the saleTransactionId values with get_available_lots and reassign_sale_lots.',
    inputSchema: {
      type: 'object',
      properties: {
        accountId: { type: 'string', description: 'Luminaria account UUID.' },
        symbol: { type: 'string', description: 'Stock ticker or OCC option symbol.' },
      },
      required: ['accountId', 'symbol'],
    },
  },
  get_available_lots: {
    name: 'get_available_lots',
    access: 'read',
    description:
      'The open lots a sale can draw from on its trade date (lot ID, acquisition date, remaining quantity, per-share and total basis). Use before reassign_sale_lots with method specific_lot to choose which lots the sale consumes.',
    inputSchema: {
      type: 'object',
      properties: {
        saleTransactionId: { type: 'string', description: 'Transaction UUID of the sale.' },
      },
      required: ['saleTransactionId'],
    },
  },
  get_corporate_actions_needing_review: {
    name: 'get_corporate_actions_needing_review',
    access: 'read',
    description:
      'Split, merger and spinoff transactions the lot engine could not resolve automatically (status needs_review), newest first. Cost basis for each affected symbol is unreliable until they are resolved with resolve_corporate_action. Each row has the transaction ID, account, symbol, action, dates, quantity, amount, price, description and any ratio/relatedSymbol already set.',
    inputSchema: {
      type: 'object',
      properties: {
        accountId: { type: 'string', description: 'Optional account UUID to filter to.' },
      },
      required: [],
    },
  },
  reassign_sale_lots: {
    name: 'reassign_sale_lots',
    access: 'write',
    description:
      "Change which lots one sale consumes. method fifo/lifo/hifo/average applies that method to this sale (clearing any stored specific-lot pick); method specific_lot requires assignments listing the lots and quantities to sell (lot IDs from get_available_lots, quantities summing to the sale quantity). The symbol's later sales replay so wash sales and holding terms stay consistent, and tax estimates are invalidated. Fails without saving if the pick cannot be applied.",
    inputSchema: {
      type: 'object',
      properties: {
        saleTransactionId: { type: 'string', description: 'Transaction UUID of the sale.' },
        method: { type: 'string', enum: LOT_METHODS, description: 'Lot-matching method for this sale.' },
        assignments: {
          type: 'array',
          description: 'Required when method is specific_lot: the lots to sell.',
          items: {
            type: 'object',
            properties: {
              lotId: {
                type: 'string',
                pattern: '^(tx|manual):',
                description: 'Lot ID from get_available_lots (starts with "tx:" or "manual:").',
              },
              quantitySold: { type: 'number', exclusiveMinimum: 0, description: 'Shares taken from this lot.' },
            },
            required: ['lotId', 'quantitySold'],
          },
        },
      },
      required: ['saleTransactionId', 'method'],
    },
  },
  recalculate_lots: {
    name: 'recalculate_lots',
    access: 'write',
    description:
      "Rebuild lot assignments from transaction history. With symbol: replays that symbol and applies method to EVERY sale of it, replacing per-sale methods chosen earlier (method required). Without symbol: replays the whole account; method (default fifo) applies only to sales with no stored per-sale choice, so earlier choices and specific-lot picks are kept. Returns counts, warnings (including unresolved corporate actions) and wash-sale totals. Invalidates tax estimates.",
    inputSchema: {
      type: 'object',
      properties: {
        accountId: { type: 'string', description: 'Luminaria account UUID.' },
        symbol: { type: 'string', description: 'Optional symbol to limit the recalculation to.' },
        method: { type: 'string', enum: LOT_METHODS, description: 'Lot-matching method (see description).' },
      },
      required: ['accountId'],
    },
  },
  update_holding_cost_basis: {
    name: 'update_holding_cost_basis',
    access: 'write',
    description:
      "Choose where a holding's cost basis comes from and/or mark it reviewed. costBasisSource calculated recomputes basis from transactions and manual lots; imported restores the broker-reported basis; manual stores the costBasis you pass (total dollars for the position, not per share). Holding IDs come from get_cost_basis_report or get_portfolio_holdings.",
    inputSchema: {
      type: 'object',
      properties: {
        holdingId: { type: 'string', description: 'Holding UUID.' },
        costBasisSource: { type: 'string', enum: COST_BASIS_SOURCES, description: 'Basis source to use.' },
        costBasis: {
          type: 'number',
          minimum: 0,
          description: 'Total cost basis in dollars. Required when costBasisSource is manual.',
        },
        costBasisMethod: { type: 'string', enum: ['fifo', 'average'], description: 'Optional calculation method.' },
        costBasisReviewed: { type: 'boolean', description: 'Mark the holding\'s basis as reviewed (or not).' },
      },
      required: ['holdingId', 'costBasisSource'],
    },
  },
  add_manual_lot: {
    name: 'add_manual_lot',
    access: 'write',
    description:
      'Add a manual tax lot to a holding, e.g. shares transferred in or bought before the imported history begins. Sets the holding\'s basis source to manual and recalculates its cost basis with the new lot.',
    inputSchema: {
      type: 'object',
      properties: {
        holdingId: { type: 'string', description: 'Holding UUID.' },
        date: { type: 'string', pattern: '^\\d{4}-\\d{2}-\\d{2}$', description: 'Acquisition date (YYYY-MM-DD).' },
        quantity: { type: 'number', exclusiveMinimum: 0, description: 'Shares in the lot.' },
        pricePerShare: { type: 'number', minimum: 0, description: 'Cost per share in dollars.' },
      },
      required: ['holdingId', 'date', 'quantity', 'pricePerShare'],
    },
  },
  remove_manual_lot: {
    name: 'remove_manual_lot',
    access: 'write',
    description:
      'Remove a manual lot from a holding and recalculate its cost basis. If no manual lots remain, a manual basis source reverts to calculated.',
    inputSchema: {
      type: 'object',
      properties: {
        holdingId: { type: 'string', description: 'Holding UUID.' },
        lotId: {
          type: 'string',
          description: 'Manual lot ID: the transactionId of an isManual lot in get_cost_basis_report.',
        },
      },
      required: ['holdingId', 'lotId'],
    },
  },
  resolve_corporate_action: {
    name: 'resolve_corporate_action',
    access: 'write',
    description:
      "Resolve or ignore a split, merger or spinoff from get_corporate_actions_needing_review; the account's lots then recalculate. What ratio means depends on the action. Split: new shares per old share (2-for-1 = 2, 1-for-10 reverse = 0.1). Merger (row is under the OLD symbol): relatedSymbol = successor symbol, ratio = successor shares per old share; a cash component comes from the transaction amount, and for a cash-and-stock deal the transaction's price must hold the successor share's fair market value (set it with update_transaction first). Spinoff (row is under the NEW child symbol, quantity = child shares received): relatedSymbol = parent symbol, ratio = fraction of the parent's basis allocated to the child, between 0 and 1 (from the issuer's Form 8937, e.g. 0.12). resolution ignored drops the row from basis calculations entirely; use it only for duplicates or rows that are not real corporate actions. Confirm ratios with the user; they are not derivable from transaction data.",
    inputSchema: {
      type: 'object',
      properties: {
        transactionId: { type: 'string', description: 'Transaction UUID of the split/merger/spinoff.' },
        resolution: { type: 'string', enum: ['resolved', 'ignored'] },
        ratio: {
          type: 'number',
          exclusiveMinimum: 0,
          description: 'See description. Required to resolve a split or spinoff; for a merger, required unless it is all cash.',
        },
        relatedSymbol: {
          type: 'string',
          description: 'Merger: successor symbol. Spinoff: parent symbol. Not used for splits.',
        },
      },
      required: ['transactionId', 'resolution'],
    },
  },
} satisfies Record<string, ToolDefinition>;
